import { isInteger, padEnd } from 'lodash-es'
import { Decimal } from 'decimal.js'
import { formatNumberStringFunc } from 'hooks/useDirection'
import LocalCache, { CacheKey } from '../util/LocalCache'
import { number2Thousands } from '../util/Order'
import { isBrowserEnv } from '@app/utils/constants'
import { useSSRLocale } from '@app/utils/common'
import { getPriceStep } from './checkers'

/**
 * 格式化数字 精度处理
 * @param number
 * @param decimal
 * @returns
 */
export const intercept = (number: number, decimal = 8) => {
    if (isNaN(number) || !isFinite(number)) {
        number = 0
    }
    let numStr = String(number)
    if (isInteger(number)) {
        // 整数补零
        if (decimal > 0) {
            numStr += '.'
            numStr = padEnd(numStr, decimal + numStr.length, '0')
        }
    } else {
        // 科学计数法
        if (numStr.indexOf('e-') >= 0 || numStr.indexOf('E-') >= 0) {
            numStr = Number(number).toFixed(decimal + 1)
        }
        let [integerPart, decimalPart] = numStr.split('.')
        decimalPart = decimalPart || ''
        if (decimalPart.length < decimal) {
            numStr = padEnd(numStr, numStr.length + decimal - decimalPart.length, '0')
        } else {
            numStr = decimal > 0 ? `${integerPart}.${decimalPart.substr(0, decimal)}` : integerPart
        }
    }
    return numStr
}

/**
 * 数字转换为千分位格式
 * @param value
 * @param accuracy 精度
 * @param suffix 可能的后缀
 * @returns {string}
 */
export const toThousands = (value: number | string, accuracy = 0, suffix?: string) => {
    const amount = intercept(Number(value), accuracy)
    let regexp = '(\\d)(?=(\\d{3})+\\.)'
    if (amount.indexOf('.') === -1) {
        regexp = '(\\d)(?=(\\d{3})+$)'
    }
    let result = amount.replace(new RegExp(regexp, 'g'), '$1,')
    return result + (suffix ? ' ' + suffix : '')
}

/**
 * 几种合约名称的形式
 */
const ContractNameModeMap = {
    0: '-',
    1: '_',
    2: '/'
}

/**
 * 格式化合约基本名称
 * @param info
 * @param mode 分隔模式
 * @param suffix 后缀
 */
export const toContractName = (info: ContractInfo | MixTickerInfo, mode = 2, suffix = '') => {
    return info.baseSymbol + ContractNameModeMap[mode] + info.pricedSymbol + suffix
}

/**
 * 把数字或者字符串格式化为带符号的
 * @param o 需要格式化的内容
 * @param text 缺省值
 * @param signed 是否需要符号
 * @param direction 页面dir值
 */
export const toFormattedChange = (o: any, text = '', signed = true) => {
    if (!o) {
        return text
    }

    let n = Number(o)
    if (isNaN(n)) {
        return null
    }

    const str = formatNumberStringFunc((n > 0 && signed ? '+' : '') + String(o), '%')
    return str
}

/**
 * 把数字或者字符串格式化为带符号的
 * @param o 需要格式化的内容
 * @param text 缺省值
 * @param signed 是否需要符号
 * @param direction 页面dir值
 */
export const toFormattedChangeNoSymbol = (o: any, text = '', signed = true) => {
    if (!o) {
        return text
    }

    let n = Number(o)
    if (isNaN(n)) {
        return null
    }

    const str = formatNumberStringFunc((n > 0 && signed ? '+' : '') + String(o))
    return str
}

/**
 * 格式化时间戳
 * @param timeStr
 * @param format
 * @param defaultText 默认输出
 */
export function toFormattedTime(timeStr: string | number, format: string, defaultText = '') {
    if (!timeStr) {
        return defaultText
    }
    let myDate = new Date(Number(timeStr))
    if (isNaN(myDate.getTime())) return timeStr
    const o = {
        'M+': myDate.getMonth() + 1, // month,
        'd+': myDate.getDate(), // day
        'h+': myDate.getHours(), // hour
        'H+': myDate.getHours(), // hour
        'm+': myDate.getMinutes(), // minute
        's+': myDate.getSeconds(), // second
        'q+': Math.floor((myDate.getMonth() + 3) / 3), // quarter
        S: myDate.getMilliseconds() // millisecond
    }
    if (/(y+)/.test(format))
        format = format.replace(RegExp.$1, (myDate.getFullYear() + '').substr(4 - RegExp.$1.length))
    for (let k in o) {
        if (new RegExp('(' + k + ')').test(format)) {
            format = format.replace(
                RegExp.$1,
                RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
            )
        }
    }
    return format
}

type RangeItem = {
    min: number
    max: number | null
    sign: string
}

const Range: RangeItem[] = [
    { min: 0, max: 1000, sign: '' },
    { min: 1000, max: 1000000, sign: 'K' },
    { min: 1000000, max: 1000000000, sign: 'M' },
    { min: 1000000000, max: 1000000000000, sign: 'B' },
    { min: 1000000000000, max: null, sign: 'T' }
]

/**
 * 中文本土化
 */
const RangeForZhCN: RangeItem[] = [
    { min: 0, max: 10000, sign: '' },
    { min: 10000, max: 100000000, sign: '万' },
    { min: 100000000, max: 1000000000000, sign: '亿' },
    { min: 1000000000000, max: null, sign: '万亿' }
]

/**
 * 中文本土化
 */
const RangeForZhTW: RangeItem[] = [
    { min: 0, max: 10000, sign: '' },
    { min: 10000, max: 100000000, sign: '萬' },
    { min: 100000000, max: 1000000000000, sign: '億' },
    { min: 1000000000000, max: null, sign: '兆' }
]

function getRange(locale: string) {
    switch (locale) {
        case 'zh-CN':
            return RangeForZhCN
        case 'zh-TW':
            return RangeForZhTW
        default:
            return Range
    }
}

/**
 * 为公共或做准备，纯函数，不包含任何项目耦合逻辑
 * @param value
 * @param digit
 * @param defaultDigit
 * @param locale
 */
export function toShortNumberBase(
    value: Decimal.Value,
    digit: number,
    defaultDigit: number,
    locale?: string
) {
    if (!value || Decimal.isDecimal(value)) return { value, sign: '' }
    let dValue = new Decimal(value)
    digit = digit || 2
    locale = locale || 'en'
    let range = getRange(locale).find(e => {
        if (e.max) {
            return dValue.greaterThanOrEqualTo(e.min) && dValue.lessThan(e.max)
        } else {
            return dValue.greaterThanOrEqualTo(e.min)
        }
    }) || { min: 0, max: null, sign: '' }

    let min = range.min,
        sign = range.sign
    if (min === 0) {
        min = 1
        digit = defaultDigit
    }

    let big = dValue.div(min)

    return {
        value: big.toFixed(digit, Decimal.ROUND_FLOOR),
        sign
    }
}

/**
 * 原始toShortNumber函数
 * @param value
 * @param digit 格式化后的小数位数 （带 sign 的时候）
 * @param defaultDigit 不需要缩短的时候的小数（不带 sign 的时候）位数
 * @param locale
 */
export function toShortNumber(
    value: Decimal.Value,
    digit: number,
    defaultDigit: number,
    locale?: string
): string {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ssrLocale = useSSRLocale()
    locale = locale || (isBrowserEnv ? (LocalCache.get(CacheKey.LOCALE) as string) : ssrLocale)
    let { value: result, sign } = toShortNumberBase(value, digit, defaultDigit, locale)
    const res = ''.concat(number2Thousands(result)).concat(sign)
    return res
}

// 获取两个时间戳之前的天、时、分、秒
export const intervalTime = (startTime = 0, endTime = 0, diffSecs = 0) => {
    let date1 = startTime || Date.now()
    let date2 = endTime //结束时间
    let date3 = diffSecs || date2 - date1 //时间差的毫秒数
    if (!date3) {
        return {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0
        }
    }
    //计算出相差天数
    let days = Math.floor(date3 / (24 * 3600 * 1000))
    //计算出小时数
    let leave1 = date3 % (24 * 3600 * 1000) //计算天数后剩余的毫秒数
    let hours = Math.floor(leave1 / (3600 * 1000))
    //计算相差分钟数
    let leave2 = leave1 % (3600 * 1000) //计算小时数后剩余的毫秒数
    let minutes = Math.floor(leave2 / (60 * 1000))
    //计算相差秒数
    let leave3 = leave2 % (60 * 1000) //计算分钟数后剩余的毫秒数
    let seconds = Math.round(leave3 / 1000)

    return {
        days,
        hours,
        minutes,
        seconds
    }
}

// 去除结尾所有多余 0
// 科学计数法也可以哟
export function removeEndZero(num) {
    let n = Number(num)
    if (isNaN(n)) return num
    let m: any = n.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/)
    m = n.toFixed(Math.max(0, (m[1] || '').length - m[2]))
    return m
}

//判断小数
export const toNonExponential = (num: number) => {
    var m = num.toExponential().match(/\d(?:.(\d*))?e([+-]\d+)/)
    //@ts-ignore
    return num.toFixed(Math.max(0, (m[1] || '').length - m[2]))
}

/**
 * 格式化币价，swap扒过来的
 * @param price 价格
 *
 */
export const formatSwapPrice = (price: string | number) => {
    const newPrice = new Decimal(price)
    if (newPrice.isNaN()) return ''
    if (newPrice.toNumber() === 0) return '0.00'
    // 价格大于等于1U的代币，四舍五入后，显示小数点后2位
    if (newPrice.toNumber() >= 1) {
        return number2Thousands(newPrice.toFixed(2))
    } else {
        // 小数点后面是否以0开头,不为0，直接显示4位
        const smallPrice = toNonExponential(newPrice.toNumber())
        let index = smallPrice.indexOf('.')
        if (smallPrice[index + 1] != '0') {
            return newPrice.toFixed(4)
        }
        // 计算小数点后连续0的个数
        // 0.01234      -> 1
        // 0.00001234   -> 4
        let zeroLength = 0
        // 保留小数点前面的值
        let preNum = smallPrice.split('.')[0]
        // 截取小数点后的值，并且转成array，计算zeroLength的长度
        let zeroPriceArr = smallPrice.substring(index + 1).split('')
        for (let i = 0; i < zeroPriceArr.length; i++) {
            if (zeroPriceArr[i] === '0') {
                zeroLength += 1
            } else {
                break
            }
        }
        let fixedNum = zeroLength + 4
        // 四舍五入后的值
        let value = newPrice.toFixed(fixedNum)
        let zeroIdx = value.indexOf('.')
        let nextNum = value.substring(zeroIdx + 1 + zeroLength)
        // 求出转换后是否有进制的情况存在
        // 示例
        // 0.00009999 -> 0.00010000
        let newZeroLength = 0
        // 保留小数点前面的值
        // let newPreNum = value.split(".")[0];
        // 截取小数点后的值，并且转成array，计算zeroLength的长度
        let newZeroPriceArr = value.substring(index + 1).split('')
        for (let i = 0; i < newZeroPriceArr.length; i++) {
            if (newZeroPriceArr[i] === '0') {
                newZeroLength += 1
            } else {
                break
            }
        }
        // 示例
        // 0.00001234 -> 0.0{4}1234
        // 0.0001234 -> 0.0001234
        if (zeroLength >= 6 && newZeroLength == zeroLength) {
            return `${preNum}.0{${zeroLength}}${nextNum}`
        } else {
            return value
        }
        // 后置除了连续0的数字
        // return Number(newPrice).toFixed(fixedNum)
    }
}

/**
 * 跟下面的 getPriceChecker 内部的 formatter 一致 只是单独抽出来
 * */
export function getPriceFormatter({ delegatePricePlace, priceEndStep }) {
    return function (value) {
        const PriceStep = getPriceStep(delegatePricePlace, priceEndStep)
        if (!new Decimal(value).mod(PriceStep).isZero()) {
            let inputPrice = new Decimal(value),
                littlePart = inputPrice.toDP(Number(delegatePricePlace) - 1, Decimal.ROUND_FLOOR),
                formatPart = inputPrice.toDP(delegatePricePlace, Decimal.ROUND_DOWN),
                stepPart = formatPart
                    .sub(littlePart)
                    .div(priceEndStep)
                    .toDP(delegatePricePlace, Decimal.ROUND_DOWN)
                    .times(priceEndStep)
                    .toDP(delegatePricePlace)
            return littlePart.add(stepPart).toFixed(delegatePricePlace)
        } else {
            return value
        }
    }
}

export const checkParamsEmptyOrZero = params => {
    return Object.values(params).some(
        i => i == undefined || i == '' || Number.isNaN(i) || Number(i) === 0
    )
}

export const getRoundDown = (num, digit) => {
    return new Decimal(num).toDecimalPlaces(digit, Decimal.ROUND_DOWN)
}

export const getRoundUp = (num, digit) => {
    return new Decimal(num).toDecimalPlaces(digit, Decimal.ROUND_UP)
}

/**
 * 向上取整 可以整数倍取整
 * @param {number} vol
 * @param {number} precision 精度 0.1 0.01 0.001 10 100 1000 为最小精度
 *
 * */
export const getRoundUpByPrecision = ({ vol, digit = 0, countMultiplier = 0 }) => {
    if (checkParamsEmptyOrZero({ vol })) {
        return 0
    }
    const res = digit ? getRoundUp(vol, digit) : new Decimal(vol)

    if (!countMultiplier) {
        return res
    } else {
        return res.dividedBy(countMultiplier).ceil().times(countMultiplier)
    }
}

export const getRoundDownByPrecision = ({
    vol,
    digit = 0,
    countMultiplier = '0'
}: {
    vol: number | string
    digit?: number
    countMultiplier?: number | string
}): Decimal => {
    if (checkParamsEmptyOrZero({ vol })) {
        return new Decimal(0)
    }
    const res = digit ? getRoundDown(vol, digit) : new Decimal(vol)
    if (!Number(countMultiplier)) {
        return res
    } else {
        return res.dividedBy(countMultiplier).floor().times(countMultiplier)
    }
}

// 根据价格乘数进行四舍五入处理的函数
export const getRoundByPriceMultiplier = ({
    vol,
    countMultiplier = '0'
}: {
    vol: number | string
    digit?: number
    countMultiplier?: number | string
}): Decimal => {
    if (checkParamsEmptyOrZero({ vol })) {
        return new Decimal(0)
    }

    const res = new Decimal(vol)

    if (!Number(countMultiplier)) {
        return res
    } else {
        // 使用乘数进行四舍五入
        return res.dividedBy(countMultiplier).round().times(countMultiplier)
    }
}

// 根据价格乘数进行向上取整
export const getRoundUpByPriceMultiplier = ({
    vol,
    countMultiplier = '0'
}: {
    vol: number | string
    digit?: number
    countMultiplier?: number | string
}): Decimal => {
    if (checkParamsEmptyOrZero({ vol })) {
        return new Decimal(0)
    }

    const res = new Decimal(vol)

    if (!Number(countMultiplier)) {
        return res
    } else {
        // 使用乘数进行向上取整
        return res.dividedBy(countMultiplier).ceil().times(countMultiplier)
    }
}
